import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import { sessionApi } from '../services/api';
import '../styles/SessionBrowse.css';

const ClientSessionBrowsePage = () => {
  const navigate = useNavigate();
  const socket = useSocket();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    topic: '',
    mode: '',
    price: '',
    page: 1,
    limit: 12,
  });
  const [booking, setBooking] = useState({});

  // ✅ Fetch sessions on mount and when filters change
  useEffect(() => {
    fetchSessions();
  }, [filters]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await sessionApi.getAll(filters);
      setSessions(response.sessions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Real-time session updates via Socket.IO
  useEffect(() => {
    if (!socket) return;

    socket.emit('session:subscribe');

    socket.on('session:created', (data) => {
      // Add new session to list if it matches filters
      setSessions((prev) => [data.session, ...prev]);
    });

    socket.on('session:updated', (data) => {
      // Update session in list
      setSessions((prev) =>
        prev.map((s) => (s._id === data.sessionId ? data.session : s))
      );
    });

    socket.on('session:cancelled', (data) => {
      // Remove cancelled session
      setSessions((prev) => prev.filter((s) => s._id !== data.sessionId));
    });

    socket.on('session:booked', (data) => {
      // Update session participant count
      setSessions((prev) =>
        prev.map((s) =>
          s._id === data.sessionId
            ? { ...s, currentParticipants: data.currentParticipants }
            : s
        )
      );
    });

    socket.on('session:booking-cancelled', (data) => {
      // Update session participant count
      setSessions((prev) =>
        prev.map((s) =>
          s._id === data.sessionId
            ? { ...s, currentParticipants: data.currentParticipants }
            : s
        )
      );
    });

    return () => {
      socket.emit('session:unsubscribe');
      socket.off('session:created');
      socket.off('session:updated');
      socket.off('session:cancelled');
      socket.off('session:booked');
      socket.off('session:booking-cancelled');
    };
  }, [socket]);

  // ✅ Handle booking - navigate to payment page
  const handleBookSession = (sessionId) => {
    navigate(`/session-payment/${sessionId}`);
  };

  // ✅ Format date & time
  const formatDateTime = (date) => {
    const options = { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(date).toLocaleDateString('en-US', options);
  };

  // ✅ Calculate duration
  const getDuration = (startDate, endDate) => {
    const mins = (new Date(endDate) - new Date(startDate)) / (1000 * 60);
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="session-browse-container">
      <div className="browse-header">
        <h1>🎓 Therapist Sessions</h1>
        <p>Join live sessions with mental health professionals</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <select
          value={filters.topic}
          onChange={(e) => setFilters({ ...filters, topic: e.target.value, page: 1 })}
          className="filter-select"
        >
          <option value="">All Topics</option>
          <option value="anxiety">Anxiety</option>
          <option value="depression">Depression</option>
          <option value="stress-management">Stress Management</option>
          <option value="mindfulness">Mindfulness</option>
          <option value="meditation">Meditation</option>
          <option value="relaxation">Relaxation</option>
          <option value="wellness">Wellness</option>
        </select>

        <select
          value={filters.mode}
          onChange={(e) => setFilters({ ...filters, mode: e.target.value, page: 1 })}
          className="filter-select"
        >
          <option value="">All Modes</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>

        <select
          value={filters.price}
          onChange={(e) => setFilters({ ...filters, price: e.target.value, page: 1 })}
          className="filter-select"
        >
          <option value="">All Prices</option>
          <option value="free">Free</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Sessions Grid */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading sessions...</p>
        </div>
      ) : sessions.length === 0 ? (
        <div className="no-sessions">
          <p>No sessions available</p>
        </div>
      ) : (
        <div className="sessions-grid">
          {sessions.map((session) => (
            <div key={session._id} className="session-card">
              {/* Header */}
              <div className="session-card-header">
                <h3>{session.title}</h3>
                <span className={`badge ${session.mode}`}>{session.mode.toUpperCase()}</span>
              </div>

              {/* Therapist Info */}
              <div className="therapist-info">
                <p>
                  👨‍⚕️ <strong>{session.therapistId?.name || 'Therapist'}</strong>
                </p>
              </div>

              {/* Description */}
              <p className="session-description">{session.description}</p>

              {/* Details */}
              <div className="session-details">
                <div className="detail-item">
                  <span>📅</span>
                  <span>{formatDateTime(session.scheduledAt)}</span>
                </div>
                <div className="detail-item">
                  <span>⏱️</span>
                  <span>{getDuration(session.scheduledAt, session.endsAt)}</span>
                </div>
                <div className="detail-item">
                  <span>👥</span>
                  <span>
                    {session.currentParticipants}/{session.maxParticipants}
                  </span>
                </div>
                {session.topic && (
                  <div className="detail-item">
                    <span>🏷️</span>
                    <span>{session.topic}</span>
                  </div>
                )}
              </div>

              {/* Capacity Bar */}
              <div className="capacity-bar">
                <div
                  className="capacity-filled"
                  style={{
                    width: `${(session.currentParticipants / session.maxParticipants) * 100}%`,
                  }}
                ></div>
              </div>

              {/* Price & Book */}
              <div className="session-card-footer">
                <div className="price">
                  {session.price === 0 ? (
                    <span className="free-badge">FREE</span>
                  ) : (
                    <span className="price-text">₹{session.price}</span>
                  )}
                </div>

                <button
                  onClick={() => handleBookSession(session._id)}
                  disabled={session.isFull || booking[session._id] === 'loading'}
                  className={`btn-book ${session.isFull ? 'disabled' : ''} ${
                    booking[session._id] === 'success' ? 'success' : ''
                  }`}
                >
                  {booking[session._id] === 'loading'
                    ? 'Booking...'
                    : booking[session._id] === 'success'
                    ? '✓ Booked'
                    : session.isFull
                    ? 'Full'
                    : 'Book Now'}
                </button>
              </div>

              {/* Zoom Link */}
              {session.mode === 'online' && session.zoomMeetingLink && (
                <div className="zoom-link">
                  <a href={session.zoomMeetingLink} target="_blank" rel="noopener noreferrer">
                    Join on Zoom 🔗
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientSessionBrowsePage;
